import { collections, firestoreHelpers, storageHelpers } from '@/lib/firebase';
import type { Passenger, User, Location } from '@/types';

export const userService = {
  // Create new passenger
  async createPassenger(userId: string, data: Partial<Passenger>): Promise<void> {
    const passengerData: Partial<Passenger> = {
      ...data,
      type: 'passenger',
      rating: 0,
      totalRides: 0,
      walletBalance: 0,
      isActive: true,
      createdAt: new Date(),
      updatedAt: new Date(),
    };
    
    await firestoreHelpers.createDocument(collections.passengers, passengerData, userId);
  },

  // Get passenger by ID
  async getPassenger(userId: string): Promise<Passenger | null> {
    return await firestoreHelpers.getDocument<Passenger>(collections.passengers, userId);
  },

  // Update passenger
  async updatePassenger(userId: string, data: Partial<Passenger>): Promise<void> {
    await firestoreHelpers.updateDocument(collections.passengers, userId, data);
  },

  // Subscribe to passenger changes
  subscribeToPassenger(userId: string, callback: (passenger: Passenger | null) => void) {
    return firestoreHelpers.subscribeToDocument<Passenger>(
      collections.passengers,
      userId,
      callback
    );
  },

  // Upload profile photo
  async uploadProfilePhoto(userId: string, file: File, onProgress?: (progress: number) => void): Promise<string> {
    const path = `passengers/${userId}/profile.jpg`;
    const photoURL = await storageHelpers.uploadFile(file, path, onProgress);
    await this.updatePassenger(userId, { photoURL });
    return photoURL;
  },

  // Update wallet balance
  async updateWalletBalance(userId: string, amount: number): Promise<void> {
    const passenger = await this.getPassenger(userId);
    if (passenger) {
      const newBalance = (passenger.walletBalance || 0) + amount;
      await this.updatePassenger(userId, { walletBalance: newBalance });
    }
  },

  // Add favorite location
  async addFavoriteLocation(userId: string, location: Location): Promise<void> {
    const passenger = await this.getPassenger(userId);
    if (passenger) {
      const favorites = passenger.favoriteLocations || [];
      favorites.push(location);
      await this.updatePassenger(userId, { favoriteLocations: favorites });
    }
  },

  // Remove favorite location
  async removeFavoriteLocation(userId: string, locationId: string): Promise<void> {
    const passenger = await this.getPassenger(userId);
    if (passenger && passenger.favoriteLocations) {
      const favorites = passenger.favoriteLocations.filter(l => l.id !== locationId);
      await this.updatePassenger(userId, { favoriteLocations: favorites });
    }
  },

  // Set home location
  async setHomeLocation(userId: string, location: Location): Promise<void> {
    await this.updatePassenger(userId, { homeLocation: location });
  },

  // Set work location
  async setWorkLocation(userId: string, location: Location): Promise<void> {
    await this.updatePassenger(userId, { workLocation: location });
  },

  // Update FCM token
  async updateFcmToken(userId: string, token: string): Promise<void> {
    await this.updatePassenger(userId, { fcmToken: token });
  },

  // Get all passengers (for admin)
  async getAllPassengers(limitCount?: number): Promise<Passenger[]> {
    return await firestoreHelpers.queryDocuments<Passenger>(
      collections.passengers,
      [],
      'createdAt',
      'desc',
      limitCount
    );
  },

  // Search passengers (for admin)
  async searchPassengers(searchTerm: string): Promise<Passenger[]> {
    const passengers = await this.getAllPassengers(100);
    return passengers.filter(p => 
      p.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
      p.phone.includes(searchTerm)
    );
  },

  // Deactivate passenger
  async deactivatePassenger(userId: string): Promise<void> {
    await this.updatePassenger(userId, { isActive: false });
  },

  // Reactivate passenger
  async reactivatePassenger(userId: string): Promise<void> {
    await this.updatePassenger(userId, { isActive: true });
  },

  // Update rating
  async updateRating(userId: string, newRating: number): Promise<void> {
    const passenger = await this.getPassenger(userId);
    if (passenger) {
      const totalRides = passenger.totalRides || 1;
      const currentRating = passenger.rating || 0;
      const updatedRating = ((currentRating * (totalRides - 1)) + newRating) / totalRides;
      await this.updatePassenger(userId, { rating: Math.round(updatedRating * 10) / 10 });
    }
  },

  // Increment total rides
  async incrementTotalRides(userId: string): Promise<void> {
    const passenger = await this.getPassenger(userId);
    if (passenger) {
      await this.updatePassenger(userId, { 
        totalRides: (passenger.totalRides || 0) + 1 
      });
    }
  }
};
